import type { SchemaOrgNode } from './schema-org-node.model';

export interface OrganizationSchemaData {
  /** Absolute URL of the site root (e.g. https://www.aceable.com/). Trailing slash optional. */
  siteUrl: string;
  /** Brand / organization display name (e.g. "Aceable"). */
  organizationName: string;
}

const ORG_ID_SUFFIX = '#organization';

export function getOrganizationId(siteUrl: string): string {
  const normalized = siteUrl.endsWith('/') ? siteUrl : `${siteUrl}/`;
  return `${normalized}${ORG_ID_SUFFIX}`;
}

/**
 * Canonical site root for a brand, derived from `brandConfig.url`
 * (e.g. "www.aceable.com" → "https://www.aceable.com/"). Returns only the host
 * rooted at `https://{host}/` — any accidental protocol, path, query, or hash is
 * discarded so Organization/WebSite `@id`s never fragment (e.g. a stray
 * "https://www.aceable.com/drivers-ed/" must still resolve to the site root).
 *
 * Returns `''` (empty) when `brandUrl` is missing/empty so callers can detect the
 * misconfiguration and omit/fall back, rather than emitting an invalid `https:///`
 * root that would propagate into Organization/WebSite `@id`s.
 */
export function getBrandSiteUrl(brandUrl: string): string {
  // Defensive: never throw if a brand config is missing/malformed `url` — a thrown
  // error here would break the whole SSR render (callers run in ngOnInit/resolvers).
  const raw = String(brandUrl ?? '').trim();
  if (!raw) return '';
  // Normalize to a parseable absolute URL so only the host is extracted; brandConfig.url
  // is usually a bare host but may arrive with a protocol and/or path.
  const withProtocol = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  try {
    const { host } = new URL(withProtocol);
    return host ? `https://${host}/` : '';
  } catch {
    // Fallback: strip protocol, take the segment before the first slash.
    const host = raw.replace(/^https?:\/\//i, '').split('/')[0];
    return host ? `https://${host}/` : '';
  }
}

/**
 * Canonical Organization `@id` for a brand — always the site ROOT
 * (`https://{brandHost}/#organization`). Every emitter (Course.provider,
 * Article.author/publisher, the Trustpilot rich node, and the finalize
 * fallback) MUST use this so the graph dedups to a single Organization entity.
 *
 * Never derive the org `@id` from the page URL or the runtime request URL
 * (`document.URL`): that yields per-page / per-environment ids (e.g. sandbox
 * review-app hosts, page paths) that fragment the org into multiple nodes and
 * leave `provider`/`author`/`publisher` references dangling. See EPS-3951.
 *
 * Returns `''` when `brandUrl` is missing/empty (rather than the invalid relative
 * `'/#organization'`) so callers can detect the misconfiguration and omit the ref.
 */
export function getBrandOrganizationId(brandUrl: string): string {
  const siteUrl = getBrandSiteUrl(brandUrl);
  return siteUrl ? getOrganizationId(siteUrl) : '';
}

/**
 * Returns a bare Schema.org EducationalOrganization node — name + url + @id only.
 * Used as the universal fallback so cross-references like {@code Course.provider}
 * always resolve, even on pages without Trustpilot data. Components with richer
 * data (Trustpilot AggregateRating, etc.) should register first; first-write-wins
 * dedup keeps the richer node.
 */
export function getOrganizationFallbackSchema(data: OrganizationSchemaData | null): SchemaOrgNode | null {
  if (!data || !data.siteUrl || !data.organizationName) return null;
  const siteUrl = data.siteUrl.endsWith('/') ? data.siteUrl : `${data.siteUrl}/`;
  return {
    '@type': 'EducationalOrganization',
    '@id': getOrganizationId(siteUrl),
    name: data.organizationName,
    url: siteUrl,
  };
}
